import { Link } from "react-router-dom";
import type { DialogSummary } from "@/types/dialog";
import { getLanguageName } from "@/lib/language-names";

const LEVEL_GRADIENTS: Record<string, string> = {
  A1: "from-emerald-600 to-teal-700",
  A2: "from-green-600 to-emerald-700",
  B1: "from-amber-500 to-orange-600",
  B2: "from-orange-500 to-red-600",
  C1: "from-purple-600 to-violet-700",
  C2: "from-indigo-600 to-blue-700",
};

const LEVEL_BADGES: Record<string, string> = {
  A1: "bg-emerald-500/30 text-emerald-300 border-emerald-500/30",
  A2: "bg-green-500/30 text-green-300 border-green-500/30",
  B1: "bg-amber-500/30 text-amber-300 border-amber-500/30",
  B2: "bg-orange-500/30 text-orange-300 border-orange-500/30",
  C1: "bg-purple-500/30 text-purple-300 border-purple-500/30",
  C2: "bg-indigo-500/30 text-indigo-300 border-indigo-500/30",
};

function isValidSlug(slug: string | undefined): slug is string {
  return !!slug && !slug.startsWith("-");
}

function slugToTitle(slug: string): string {
  const cleaned = slug.replace(/-[a-f0-9]{6,}$/i, "");
  return cleaned
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

interface FeedCardProps {
  dialog: DialogSummary;
}

export function FeedCard({ dialog }: FeedCardProps) {
  const langName = getLanguageName(dialog.language_code);
  const gradient = LEVEL_GRADIENTS[dialog.level] ?? "from-slate-600 to-slate-700";
  const badge = LEVEL_BADGES[dialog.level] ?? "bg-slate-500/30 text-slate-300 border-slate-500/30";
  const validSlug = isValidSlug(dialog.slug);
  const title = dialog.title_english || (validSlug ? slugToTitle(dialog.slug) : slugToTitle(dialog.scenario_code));
  const speakerNames = dialog.speakers.map((s) => s.name).join(" & ");
  const linkPath = validSlug ? dialog.slug : dialog.id;

  return (
    <Link
      to={`/dialog/${linkPath}`}
      state={{ language_code: dialog.language_code }}
      className="group block"
    >
      <div className="bg-th-surface-dim rounded-xl border border-th-border-dim overflow-hidden hover:border-slate-600 transition-colors">
        <div
          className={`aspect-[2/3] bg-gradient-to-br ${gradient} relative overflow-hidden`}
        >
          {dialog.thumbnail_url && (
            <img
              src={dialog.thumbnail_url}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-black/5 group-hover:bg-black/0 transition-colors" />

          <div className="absolute top-3 left-3 right-3 flex items-start justify-between">
            <span className="bg-black/50 backdrop-blur-sm text-white text-xs font-semibold px-2.5 py-1 rounded-lg">
              {langName}
            </span>
            <span className={`text-xs font-bold px-2.5 py-1 rounded-lg border backdrop-blur-sm ${badge}`}>
              {dialog.level}
            </span>
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 via-black/30 to-transparent">
            <h3 className="text-white font-bold text-lg leading-snug line-clamp-3 mb-1">
              {title}
            </h3>
            {dialog.title_target && (
              <p className="text-white/70 text-sm line-clamp-1">{dialog.title_target}</p>
            )}
          </div>
        </div>

        <div className="px-4 py-3">
          <span className="text-th-text-2 text-sm">{speakerNames}</span>
        </div>
      </div>
    </Link>
  );
}
